import React from "react";
import { Link } from "react-router-dom";
import {
  Waves, Calendar, TrendingUp, Wallet, Clock, Users2,
  ShieldCheck, FileText, MessageSquareText, Smartphone, ArrowRight, Star
} from "lucide-react";
import { Button } from "../components/ui/button";

/**
 * Iter35 wave 3 — dedicated marketing landing for MNS prospects.
 * Reached via the "Je suis MNS" button on the public homepage and via /landing/mns.
 */
const Stat = ({ value, label }) => (
  <div className="text-center">
    <div className="text-4xl font-bold text-sky-700 tabular-nums" style={{ fontFamily: "Outfit" }}>{value}</div>
    <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">{label}</div>
  </div>
);

const Pillar = ({ icon: Icon, title, desc, accent = "sky" }) => (
  <div className={`card-soft p-6 fade-up border-l-4 border-${accent}-500`} data-testid={`mns-landing-pillar-${title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    <div className={`w-11 h-11 rounded-xl bg-${accent}-100 text-${accent}-700 flex items-center justify-center mb-3`}><Icon className="w-5 h-5" /></div>
    <h3 className="font-bold text-slate-900 mb-1" style={{ fontFamily: "Outfit" }}>{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);

const BenefitRow = ({ icon: Icon, title, desc }) => (
  <div className="flex gap-4 items-start">
    <div className="shrink-0 w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center"><Icon className="w-5 h-5" /></div>
    <div>
      <div className="font-semibold text-slate-900">{title}</div>
      <div className="text-sm text-slate-600">{desc}</div>
    </div>
  </div>
);

export default function MnsLanding() {
  return (
    <div data-testid="mns-landing">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sky-50 via-white to-cyan-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 pt-14 pb-20 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-up">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-sky-700 bg-sky-100 px-3 py-1 rounded-full">
              <Waves className="w-3.5 h-3.5" /> Espace Maîtres-Nageurs
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900 mt-5" style={{ fontFamily: "Outfit" }}>
              Gagnez du <span className="text-sky-700">temps</span>, des <span className="text-sky-700">clients</span> et de l'<span className="text-sky-700">argent</span>.
            </h1>
            <p className="mt-5 text-lg text-slate-700 max-w-xl">
              <strong>10 heures de gestion en moins par semaine</strong>, un absentéisme divisé par 3, et une visibilité instantanée
              auprès des élèves de votre bassin. MySwimmingLessons, c'est la plateforme qui travaille <em>pour</em> vous.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/register"><Button data-testid="mns-landing-cta-register" className="rounded-full bg-sky-700 hover:bg-sky-600 px-7 py-6 text-base">
                Créer mon compte MNS<ArrowRight className="w-4 h-4 ml-2" />
              </Button></Link>
              <a href="#mns-pricing"><Button variant="outline" data-testid="mns-landing-cta-pricing" className="rounded-full px-7 py-6 text-base border-2">
                Voir les avantages
              </Button></a>
            </div>
            <div className="mt-7 flex items-center gap-6 text-sm text-slate-600 flex-wrap">
              <span className="flex items-center gap-1.5"><ShieldCheck className="w-4 h-4 text-sky-700" /> 3 mois gratuits</span>
              <span className="flex items-center gap-1.5"><Wallet className="w-4 h-4 text-sky-700" /> Paiement Stripe Connect</span>
              <span className="flex items-center gap-1.5"><FileText className="w-4 h-4 text-sky-700" /> Conforme Factur-X 2026</span>
            </div>
          </div>
          <div className="relative fade-up">
            <div className="absolute -inset-6 bg-gradient-to-br from-amber-200/50 to-sky-300/40 rounded-[2.5rem] blur-2xl" />
            <img
              alt="Maître-nageur professionnel donnant un cours dans un bassin"
              src="https://images.unsplash.com/photo-1530549387789-4c1017266635?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400"
              className="relative rounded-[2rem] shadow-2xl object-cover w-full h-[460px]"
            />
            <div className="absolute -bottom-5 -left-5 glass rounded-2xl p-4 shadow-xl flex items-center gap-3 max-w-xs">
              <div className="bg-emerald-600 text-white p-2 rounded-xl"><TrendingUp className="w-4 h-4" /></div>
              <div>
                <div className="text-[11px] text-slate-500">Revenus moyens nets</div>
                <div className="text-sm font-bold text-slate-900">+1 850€/mois en saison</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Numbers strip */}
      <section className="bg-white border-y border-slate-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-8 grid grid-cols-2 sm:grid-cols-4 gap-6">
          <Stat value="-10h" label="gestion / semaine" />
          <Stat value="+30%" label="taux de remplissage" />
          <Stat value="-65%" label="annulations clients" />
          <Stat value="3 mois" label="d'essai offerts" />
        </div>
      </section>

      {/* Pillars */}
      <section id="mns-pricing" className="max-w-7xl mx-auto px-4 sm:px-8 py-16">
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>
          Tout ce qu'il vous faut, <span className="text-sky-700">rien de superflu</span>.
        </h2>
        <p className="text-slate-600 mt-2 mb-10">La création de créneaux et la messagerie sont gratuites — l'abonnement Premium vous donne accès à plus d'outils pour une meilleure optimisation de votre temps.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          <Pillar icon={Calendar} title="Créneaux & réservations" accent="sky"
                  desc="Publication illimitée, semaines-types dupliquables, paiement en ligne ou cash, rappels J-1 automatiques. 100% gratuit, sans condition." />
          <Pillar icon={Users2} title="Onglet Clients" accent="emerald"
                  desc="Liste paginée de tous vos élèves. Fiche par client : suivi pédagogique privé (texte + photos par leçon)." />
          <Pillar icon={TrendingUp} title="CRM & Statistiques" accent="violet"
                  desc="Chiffre d'affaires, taux d'occupation, fidélité client, prévisionnel. KPIs mis à jour en temps réel." />
          <Pillar icon={FileText} title="Comptabilité Factur-X" accent="amber"
                  desc="Factures conformes à la réforme 2026 (CII XML embarqué), export FEC + récap TVA en un clic. Vos heures de tableurs, c'est fini." />
          <Pillar icon={MessageSquareText} title="Messagerie" accent="sky"
                  desc="Vos clients vous écrivent depuis leur fiche. Notifications, historique. Inclus dans la version gratuite." />
          <Pillar icon={Smartphone} title="App mobile (PWA)" accent="cyan"
                  desc="Vos clients vous trouvent, vous gérez votre planning depuis leur poche. Installable comme une vraie app." />
        </div>
      </section>

      {/* Convincing column */}
      <section className="bg-gradient-to-br from-sky-50 to-cyan-50 border-y border-sky-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 py-16 grid md:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Pourquoi rejoindre MSL ?</h2>
            <p className="mt-3 text-slate-700">
              Parce que la valeur d'un cours, c'est <strong>l'enseignement</strong>, pas la paperasse. On a construit la plateforme avec des MNS, pour des MNS.
            </p>
            <Link to="/register"><Button className="rounded-full bg-sky-700 hover:bg-sky-600 mt-6" data-testid="mns-landing-cta-register-2">Démarrer gratuitement →</Button></Link>
          </div>
          <div className="space-y-5">
            <BenefitRow icon={Clock} title="Réduisez l'absentéisme" desc="Rappels par email J-1 automatiques + paiement à la réservation : vos élèves viennent." />
            <BenefitRow icon={Wallet} title="Touchez vos paiements rapidement" desc="Stripe Connect Express : virements automatisés sur votre compte pro, sans gestion manuelle." />
            <BenefitRow icon={Star} title="Mettez-vous en avant" desc="Boost ponctuel + bandeau scrollant sur la page d'accueil client : visibilité maximale auprès des nouveaux élèves." />
            <BenefitRow icon={ShieldCheck} title="Conforme & rassurant" desc="RGPD ✓, e-invoicing 2026 ✓, vérification des diplômes par notre équipe ✓." />
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-3xl mx-auto px-4 py-14 text-center">
        <h2 className="text-3xl font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Prêt à reprendre le contrôle de votre activité ?</h2>
        <p className="text-slate-600 mt-3">Inscription en 2 minutes. 3 mois Premium offerts à votre 1ère réservation payée.</p>
        <Link to="/register"><Button data-testid="mns-landing-cta-bottom" className="rounded-full bg-gradient-to-r from-sky-600 to-cyan-600 hover:from-sky-500 hover:to-cyan-500 text-white px-9 py-7 text-lg mt-7">
          Créer mon compte MNS gratuit<ArrowRight className="w-5 h-5 ml-2" />
        </Button></Link>
      </section>
    </div>
  );
}
